import { useRef, useState } from "react";
import apiService from "services/api";
import { useHistory, useParams } from "react-router-dom";
import { useNotificationContext } from "contexts";
import { useFieldArray, useForm } from "react-hook-form";
import { FormEntry, ImageUpload } from "components";
import { FormGroup, Label, Row, Col, Button, Input } from "reactstrap";
import { locales } from "utils/locales";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import useProgressBar from "components/ProgressBar/useProgressBar";
import NestedFieldOptions from "./NestedOptions";

const EditMenuItemPage = () => {
	const history = useHistory();
	const { menu_item_id } = useParams();
	const [ProgressBar, startProgress, finishProgress] = useProgressBar();
	const [showLoading, setShowLoading] = useState(false);

	const {
		register,
		getValues,
		reset,
		control,
		watch,
		formState: { errors },
		handleSubmit,
	} = useForm();

	const { fields, append, remove } = useFieldArray({
		control: control,
		name: "options",
	});

	const removeOption = (idx) => () => remove(idx);

	const { data, isLoading, refetch } = useQuery(["menuItem", menu_item_id], () => apiService.getMenuItem(menu_item_id), {
		refetchOnwindowFocus: false,
		onSuccess: (data) => reset(data),
	});

	const { createNotification } = useNotificationContext();
	const fileInputRef = useRef();

	const startSubmit = () => {
		setShowLoading(true);
	};

	const endSubmit = () => {
		setShowLoading(false);
	};

	const submitNewMenuItemForm = async (data) => {
		startSubmit();
		var file;

		if (fileInputRef.current?.files?.length) {
			file = fileInputRef.current.files[0];
			startProgress();
		}
		try {
			const generator = apiService.updateMenuItem(menu_item_id, data, file);
			const imageUploadYield = await generator.next();
			if (imageUploadYield.value) finishProgress();
			await generator.next();
			createNotification({
				type: "success",
				message: "Successfully edited menu item",
			});
			refetch();
		} catch (error) {
			return createNotification({
				type: "danger",
				message: error.message,
			});
		} finally {
			endSubmit();
		}
	};

	return (
		<form onSubmit={handleSubmit(submitNewMenuItemForm)} className="content">
			<div className="d-flex justify-content-center">
				<div className="w-75">
					<div className="my-3">
						<Link to="/coffee/menu-item" className="btn btn-sm btn-info my-3">
							&lt; Back to menu
						</Link>
					</div>
					<h5 className="text-center mb-4">Edit Menu Item</h5>
					<div className="mb-3">
						<FormGroup className="mb-1">
							<Label for="Title" className="text-white">
								Title
							</Label>
						</FormGroup>
						{locales.map((locale, key) => (
							<div className="input-group mb-2" key={key}>
								<div className="input-group-prepend">
									<span className="input-group-text pr-2">{locale.tag}</span>
								</div>
								<input
									type="text"
									className="form-control"
									{...register(`title${locale.languageShort}`, {
										required: locale.tag === "EN" ? true : undefined,
									})}
									placeholder={`Title in ${locale.languageLong}`}
									disabled={isLoading}
								/>
							</div>
						))}
					</div>
					<div className="mb-3">
						<FormGroup className="mb-1">
							<Label for="Description" className="text-white">
								Description
							</Label>
						</FormGroup>
						{locales.map((locale, key) => (
							<div className="input-group mb-2" key={key}>
								<FormEntry
									formLabel={locale.tag}
									type="text"
									className="form-control"
									register={register}
									name={`description${locale.languageShort}`}
									required={locale.tag === "EN"}
									placeholder={`Description in ${locale.languageLong}`}
									disabled={isLoading}
									error={errors[`description${locale.languageShort}`]}
								/>
							</div>
						))}
					</div>
					<Row>
						<Col>
							<FormGroup className="mb-4">
								<Label for="exampleEmail" className="text-white">
									Price
								</Label>
								<input
									className="form-control"
									type="number"
									{...register("price", {
										required: true,
										min: 0,
									})}
									placeholder="Reward Points"
								/>
							</FormGroup>
							<FormGroup className="mb-4">
								<Label for="exampleEmail" className="text-white">
									Category
								</Label>
								<select
									className="form-control"
									{...register("category", {
										required: true,
									})}
									placeholder="Category"
								>
									<option value="Food">Food</option>
									<option value="Drinks">Drinks</option>
									<option value="Others">Others</option>
								</select>
							</FormGroup>
							<FormGroup className="mb-4">
								<Label for="exampleEmail" className="text-white">
									Sub Category
								</Label>
								<select
									className="form-control"
									{...register("subCategory", {
										required: true,
									})}
									placeholder="Sub Category"
								>
									<option disabled value="">
										Select a Category
									</option>
									<option value="Beverage">Beverage</option>
									<option value="Coffee">Coffee</option>
									<option value="Coffee Beans">Coffee Beans</option>
									<option value="Mains">Mains</option>
									<option value="Others">Others</option>
								</select>
							</FormGroup>
							<FormGroup className="mb-4">
								<Label for="exampleEmail" className="text-white">
									Sequence
								</Label>
								<input
									className="form-control"
									type="number"
									{...register("sequence", {
										min: 0,
									})}
									placeholder="Sequence in menu"
								/>
							</FormGroup>
							<Row>
								<Col>
									<FormGroup check className="mb-4">
										<Label check>
											<input type="checkbox" className="mr-2" {...register("isSoldOut")} />
											<span className="form-check-sign">
												<span className="check"></span>
											</span>
											<span className="text-white">Sold Out?</span>
										</Label>
									</FormGroup>
								</Col>
								<Col>
									<FormGroup check className="mb-4">
										<Label check>
											<input type="checkbox" className="mr-2" {...register("isFeatured")} />
											<span className="form-check-sign">
												<span className="check"></span>
											</span>
											<span className="text-white">Featured?</span>
										</Label>
									</FormGroup>
								</Col>
								<Col>
									<FormGroup check className="mb-4">
										<Label check>
											<input type="checkbox" className="mr-2" {...register("isLatest")} />
											<span className="form-check-sign">
												<span className="check"></span>
											</span>
											<span className="text-white">Latest?</span>
										</Label>
									</FormGroup>
								</Col>
								<Col>
									<FormGroup check className="mb-4">
										<Label check>
											<input type="checkbox" className="mr-2" {...register("isStaffOnly")} />
											<span className="form-check-sign">
												<span className="check"></span>
											</span>
											<span className="text-white">Staff Only?</span>
										</Label>
									</FormGroup>
								</Col>
							</Row>
						</Col>
						<Col className="text-center">
							<FormGroup>
								<Label for="exampleEmail" className="text-white">
									Image
								</Label>
							</FormGroup>
							<ImageUpload hasButton={false} fileInputRef={fileInputRef} imgPath={data?.imgPath} />
							{showLoading && <ProgressBar />}
						</Col>
					</Row>

					<FormGroup className="mb-2">
						<Label className="text-white">Options</Label>
						{fields.length ? (
							fields.map((field, i) => (
								<NestedFieldOptions
									control={control}
									register={register}
									index={i}
									id={field.id}
									watch={watch}
									key={field.id}
									namePrefix={`options.[${i}]`}
									removeOption={removeOption(i)}
									errors={errors}
								/>
							))
						) : (
							<div>No Options</div>
						)}
						<Button size="sm" color="warning" className="mt-2" type="button" onClick={() => append(1)}>
							Add Option
						</Button>
					</FormGroup>
				</div>
			</div>

			<div className="d-flex justify-content-end mt-5">
				{showLoading ? (
					<Button color="primary" disabled={true}>
						<span className="spinner-border"></span>
					</Button>
				) : (
					<Button color="primary" type="submit">
						Submit
					</Button>
				)}
				<button className="btn btn-secondary" type="button" onClick={() => reset(data)}>
					Reset
				</button>
			</div>
		</form>
	);
};

export default EditMenuItemPage;
